// src/modules/Rules/components/display/CreateRuleView.tsx
// Vista del formulario para crear o editar una regla de envío. Muestra los campos, condiciones y acciones configurables.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { CreateRuleSkeleton } from '../skeletons';

import RuleNameSection from './RuleNameSection';
import RuleConditionsSection, { Condition } from './RuleConditionsSection';
import RuleActionsSection, { ActionType } from './RuleActionsSection';

export interface CreateRuleViewProps {
  isEditMode: boolean;
  loadingRule: boolean;
  loadingCondsMeta: boolean;

  ruleName: string;
  setRuleName: (v: string) => void;

  matchAll: boolean;
  setMatchAll: (v: boolean) => void;
  conditions: Condition[];
  onAddCondition: () => void;
  onUpdateCondition: (id: number, key: keyof Condition, val: string) => void;
  onRemoveCondition: (id: number) => void;
  conditionOptions: { id: string; name: string }[];
  loadOperators: (field: string) => Promise<{ id: string; name: string }[]>;
  loadValues: (field: string) => Promise<{
    options: { id: string; name: string }[];
    valueType: 'select' | 'text' | 'numeric';
  }>;
  loadCourierProducts: (integrationId: string) => Promise<{ id: string; name: string }[]>;
  findCourierByServiceId: (serviceId: string) => Promise<string | null>;
  /** Reglas bajo /clients/:id/rules: filtra producto/tienda por cliente */
  clientTeamId?: number;

  actionType: ActionType;
  onActionTypeChange: (type: ActionType) => void;
  courierIntegrations: { id: number; name: string }[];
  courierProducts: { id: number; name: string }[];
  loadingProducts: boolean;
  selectedCourierIntegrationId: string;
  onCourierChange: (id: string) => void;
  selectedCourierProductId: string;
  onProductChange: (id: string) => void;

  onCancel: () => void;
  onSubmit: () => void;
  /** `page` = acciones fijas arriba (pantalla completa). `panel` = acciones al pie (sheet/modal). */
  layoutVariant?: 'page' | 'panel';
  /** Tarjetas con cabecera en banda (listado de reglas por cliente). Solo desde el sheet de cliente. */
  sectionVariant?: 'default' | 'clientCard';
}

const CreateRuleView: React.FC<CreateRuleViewProps> = ({
  isEditMode,
  loadingRule,
  loadingCondsMeta,

  ruleName,
  setRuleName,

  matchAll,
  setMatchAll,
  conditions,
  onAddCondition,
  onUpdateCondition,
  onRemoveCondition,
  conditionOptions,
  loadOperators,
  loadValues,
  loadCourierProducts,
  findCourierByServiceId,
  clientTeamId,

  actionType,
  onActionTypeChange,
  courierIntegrations,
  courierProducts,
  loadingProducts,
  selectedCourierIntegrationId,
  onCourierChange,
  selectedCourierProductId,
  onProductChange,

  onCancel,
  onSubmit,
  layoutVariant = 'page',
  sectionVariant = 'default',
}) => {
  const { t } = useTranslation('rulesModule');
  const isPanel = layoutVariant === 'panel';
  const sectionStyle = sectionVariant === 'clientCard' ? 'clientCard' : 'default';

  // Estado local para mantener el loading unos ms extra
  const [showLoading, setShowLoading] = useState(true);
  useEffect(() => {
    // Ajuste: permitir que la vista se renderice aunque no existan integraciones de courier
    // (couriers/integrations puede devolver 0). Solo mantenemos el loading por datos
    // estrictamente necesarios para el formulario (regla y metadatos de condiciones).
    // No bloqueamos el renderizado si conditionOptions está vacío, ya que los couriers
    // se cargan independientemente y deben mostrarse.
    if (loadingRule || loadingCondsMeta) {
      setShowLoading(true);
    } else {
      const timeout = setTimeout(() => setShowLoading(false), 0);
      return () => clearTimeout(timeout);
    }
  }, [loadingRule, loadingCondsMeta]);

  if (showLoading) {
    return <CreateRuleSkeleton />;
  }

  const actionBar = (
    <div
      className={
        isPanel
          ? 'flex flex-wrap justify-end gap-2 border-t border-border pt-4 mt-2'
          : 'flex justify-end absolute top-1 right-4 gap-2 z-10'
      }
    >
      <Button variant="outline" onClick={onCancel} size="lg">
        {t('create.cancel')}
      </Button>
      <Button variant="primary" onClick={onSubmit} size="lg">
        {isEditMode ? t('create.save_changes') : t('create.submit')}
      </Button>
    </div>
  );

  return (
    <div className={`${isPanel ? 'space-y-4 pb-1' : 'p-6 space-y-6 relative'}`} data-testid="create-rule-page">
      {!isPanel && actionBar}

      <RuleNameSection ruleName={ruleName} setRuleName={setRuleName} sectionStyle={sectionStyle} />

      <RuleConditionsSection
        conditions={conditions}
        matchAll={matchAll}
        onToggleMatchAll={setMatchAll}
        conditionOptions={conditionOptions}
        loadOperators={loadOperators}
        loadValues={loadValues}
        onUpdateCondition={onUpdateCondition}
        onRemoveCondition={onRemoveCondition}
        onAddCondition={onAddCondition}
        courierIntegrations={courierIntegrations}
        loadCourierProducts={loadCourierProducts}
        findCourierByServiceId={findCourierByServiceId}
        clientTeamId={clientTeamId}
        sectionStyle={sectionStyle}
      />

      <RuleActionsSection
        actionType={actionType}
        onActionTypeChange={onActionTypeChange}
        courierIntegrations={courierIntegrations}
        courierProducts={courierProducts}
        loadingProducts={loadingProducts}
        selectedCourierIntegrationId={selectedCourierIntegrationId}
        onCourierChange={onCourierChange}
        selectedCourierProductId={selectedCourierProductId}
        onProductChange={onProductChange}
        sectionStyle={sectionStyle}
      />

      {isPanel && actionBar}
    </div>
  );
};

export default CreateRuleView;
